import { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download, Printer } from "lucide-react";
import { LOGO_URL, BRAND_NAME } from "@/lib/brand";
import html2canvas from "html2canvas";
import { toast } from "sonner";

const PRESETS = [
  { key: "donate", label: "Donate", headline: "Fuel another mile", subline: "Every gift puts us in front of someone who still thinks they can't get sober.", cta: "Scan to donate", url: "https://donate.50statessober.org" },
  { key: "pledge", label: "Wall of Hope", headline: "Sobriety is possible.", subline: "Add your day to the wall. Anonymous-friendly. Just a first name.", cta: "Scan to pledge", url: "https://50statessober.org/stories" },
  { key: "site", label: "Main site", headline: "50 states. Fifty years. One movement.", subline: "Rick & Stephanie McEntee are driving every state to prove you're not alone.", cta: "Scan to visit", url: "https://50statessober.org" },
];

export default function QRPoster() {
  const [presetKey, setPresetKey] = useState("donate");
  const [headline, setHeadline] = useState(PRESETS[0].headline);
  const [subline, setSubline] = useState(PRESETS[0].subline);
  const [cta, setCta] = useState(PRESETS[0].cta);
  const [url, setUrl] = useState(PRESETS[0].url);
  const [theme, setTheme] = useState("light"); // light | dark
  const posterRef = useRef(null);

  const applyPreset = (key) => {
    const p = PRESETS.find((x) => x.key === key);
    if (!p) return;
    setPresetKey(key);
    setHeadline(p.headline);
    setSubline(p.subline);
    setCta(p.cta);
    setUrl(p.url);
  };

  const downloadPng = async () => {
    if (!posterRef.current) return;
    try {
      const canvas = await html2canvas(posterRef.current, { scale: 3, useCORS: true, backgroundColor: null });
      const link = document.createElement("a");
      link.download = `50-states-sober-poster-${presetKey}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Poster downloaded");
    } catch {
      toast.error("Download failed — try Print instead");
    }
  };

  const printPoster = () => {
    window.print();
  };

  const bg = theme === "dark" ? "#111111" : "#F7F1E7";
  const ink = theme === "dark" ? "#F7F1E7" : "#1F2937";
  const accent = "#C97719";
  const forest = "#0F2447";
  const inkSoft = theme === "dark" ? "rgba(247,241,231,0.7)" : "rgba(31,41,55,0.7)";

  return (
    <div data-testid="qr-poster-tab" className="space-y-8">
      <div>
        <h2 className="font-serif-display text-3xl mb-2">Printable poster generator</h2>
        <p className="text-sm text-[var(--ink-soft)]">Design a QR poster for events, meetings, cars, or flyers. Download PNG or print directly.</p>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        {/* Controls */}
        <div className="md:col-span-5 space-y-5 print:hidden">
          <div>
            <div className="text-xs font-mono-acc tracking-wider text-[var(--ink-soft)] mb-2">Preset</div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  data-testid={`preset-${p.key}`}
                  onClick={() => applyPreset(p.key)}
                  className={`px-4 py-2 rounded-full text-xs border transition ${
                    presetKey === p.key
                      ? "bg-[var(--terracotta)] text-[var(--ink)] border-[var(--terracotta)]"
                      : "bg-[var(--bg)] text-[var(--ink)] border-[var(--line)] hover:border-[var(--terracotta)]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-mono-acc tracking-wider text-[var(--ink-soft)] mb-2">Theme</div>
            <div className="flex gap-2">
              {["light", "dark"].map((t) => (
                <button
                  key={t}
                  data-testid={`theme-${t}`}
                  onClick={() => setTheme(t)}
                  className={`px-4 py-2 rounded-full text-xs border transition ${
                    theme === t
                      ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
                      : "bg-[var(--bg)] text-[var(--ink)] border-[var(--line)]"
                  }`}
                >
                  {t === "light" ? "Sand" : "Night"}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">Headline</div>
            <input data-testid="poster-headline" value={headline} onChange={(e) => setHeadline(e.target.value)}
              className="w-full px-3 py-2.5 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]" />
          </label>

          <label className="block">
            <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">Subline</div>
            <textarea data-testid="poster-subline" value={subline} onChange={(e) => setSubline(e.target.value)} rows={3}
              className="w-full px-3 py-2.5 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]" />
          </label>

          <label className="block">
            <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">QR call-to-action</div>
            <input data-testid="poster-cta" value={cta} onChange={(e) => setCta(e.target.value)}
              className="w-full px-3 py-2.5 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]" />
          </label>

          <label className="block">
            <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">Destination URL</div>
            <input data-testid="poster-url" value={url} onChange={(e) => setUrl(e.target.value)}
              className="w-full px-3 py-2.5 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)] font-mono-acc" />
          </label>

          <div className="flex gap-3 pt-4">
            <button data-testid="poster-download" onClick={downloadPng}
              className="inline-flex items-center gap-2 bg-[var(--terracotta)] text-[var(--ink)] px-5 py-2.5 rounded-full text-sm hover:bg-[var(--terracotta-dark)] transition">
              <Download className="w-4 h-4" /> Download PNG
            </button>
            <button data-testid="poster-print" onClick={printPoster}
              className="inline-flex items-center gap-2 border border-[var(--ink)] px-5 py-2.5 rounded-full text-sm hover:bg-[var(--ink)] hover:text-[var(--bg)] transition">
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
          <p className="text-xs text-[var(--ink-soft)]">Tip: Print → &quot;Save as PDF&quot; for a shareable PDF file.</p>
        </div>

        {/* Preview */}
        <div className="md:col-span-7">
          <div className="overline mb-3 print:hidden">Preview (8.5×11 portrait)</div>
          <div className="poster-print-wrap flex justify-center">
            <div
              ref={posterRef}
              data-testid="poster-preview"
              className="poster-canvas"
              style={{
                width: "8.5in",
                height: "11in",
                maxWidth: "100%",
                aspectRatio: "8.5 / 11",
                background: bg,
                color: ink,
                padding: "0.75in",
                display: "flex",
                flexDirection: "column",
                fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* subtle grain */}
              <div style={{ position: "absolute", inset: 0, opacity: 0.05, background: "radial-gradient(circle at 30% 20%, "+accent+" 0%, transparent 50%)" }} />

              {/* Logo + brand */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px", position: "relative" }}>
                <img src={LOGO_URL} alt="" style={{ width: "56px", height: "56px", borderRadius: "50%", objectFit: "cover" }} crossOrigin="anonymous" />
                <div>
                  <div style={{ fontSize: "22px", fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1 }}>{BRAND_NAME}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.25em", color: inkSoft, marginTop: "6px", textTransform: "uppercase" }}>Ask me how</div>
                </div>
              </div>

              {/* Divider */}
              <div style={{ height: "1px", background: theme === "dark" ? "rgba(247,241,231,0.15)" : "rgba(31,41,55,0.12)", margin: "0.4in 0", position: "relative" }} />

              {/* Headline */}
              <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", letterSpacing: "0.28em", color: accent, marginBottom: "18px", textTransform: "uppercase" }}>
                  A nationwide recovery movement
                </div>
                <h1 style={{ fontSize: "58px", lineHeight: 1.02, letterSpacing: "-0.02em", margin: 0, fontWeight: 500 }}>
                  {headline}
                </h1>
                <p style={{ fontSize: "18px", lineHeight: 1.5, color: inkSoft, marginTop: "22px", maxWidth: "5in", fontFamily: "'Inter', sans-serif" }}>
                  {subline}
                </p>
              </div>

              {/* QR block */}
              <div style={{ position: "relative", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "0.4in", marginTop: "0.4in" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.28em", color: accent, textTransform: "uppercase", marginBottom: "10px" }}>
                    {cta}
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "14px", color: ink, wordBreak: "break-all", lineHeight: 1.3 }}>
                    {url.replace(/^https?:\/\//, "")}
                  </div>
                  <div style={{ marginTop: "20px", fontSize: "12px", color: inkSoft, fontFamily: "'Inter', sans-serif" }}>
                    Rick &amp; Stephanie McEntee · <span style={{ color: forest }}>50statessober.org</span>
                  </div>
                </div>
                <div style={{ padding: "14px", background: "#fff", borderRadius: "4px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
                  <QRCodeSVG value={url} size={180} level="H" includeMargin={false} fgColor="#0f172a" bgColor="#ffffff" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
